import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import type { Location } from 'react-router';
import { useLastLogin, useUserActions } from 'stores/useUserStore';
import type { CurrentUser } from 'stores/useUserStore';

import type { RouterHooksHOCProps } from 'hooks/withRouter';

import LoggingService from 'components/core/logging/LoggingService';
import { RoutePath } from 'enums/routePath';
import { withRouter } from 'hooks/withRouter';
import { authManager, isLoggedIn } from 'utils/authUtils';
import { reloadAppIfOutdated } from 'utils/deviceUtils';
import { lastLoginStorage } from 'utils/storage/auth';
import { buildContinueSearch, getSafeContinuePath } from 'utils/urlUtils';

enum RouteSegment {
  TOKEN = '/\\S+$',
}

const authAgnosticPaths = [RoutePath.MOCKS, RoutePath.MOBILE_RICH_TEXT_EDITOR];

export const noAuthPaths = [
  RoutePath.FORGOT_PASSWORD,
  RoutePath.LOGIN,
  RoutePath.REQUEST_INVITE,
  RoutePath.RESET_PASSWORD + RouteSegment.TOKEN,
  ...authAgnosticPaths,
];

interface Props {
  children: ReactNode;
  router: RouterHooksHOCProps;
}

const requiresAuth = (location: Location): boolean => {
  const { pathname } = location;

  return !noAuthPaths.reduce((matches, noAuthPath) => matches || new RegExp(noAuthPath).test(pathname), false);
};

const RouterRoot = ({ children, router }: Props) => {
  const lastLogin = useLastLogin();
  const { setLastLogin, setUser } = useUserActions();
  const didMountRef = useRef(false);

  /*
   * Latest-router ref. `fetchUserInfo` reads the router AFTER awaiting `tryLogin`, but the mount
   * effect captures the first-render `fetchUserInfo` closure (deps `[]`). Since `useRouter()`
   * returns a fresh object per navigation, that closure's `router` goes stale if the location
   * changes mid-fetch — and the post-auth redirect would target the wrong page. Reading through
   * this ref restores the old class behavior (`this.props.router` was always current).
   */
  const routerRef = useRef(router);
  routerRef.current = router;

  const requiresUnauthorizedRedirect = (): boolean => {
    const location = router?.location;

    if (location && !isLoggedIn() && requiresAuth(location)) {
      /*
       * Not logged in on a page requiring valid authentication? Redirect to login, carrying the
       * intended destination in `?continue=` so `Login` can return the user there after auth.
       * This survives a hard refresh on /login, unlike the in-memory `authManager.routingTarget`.
       */
      const target = `${location.pathname}${location.search}${location.hash}`;
      router?.replace(`${RoutePath.LOGIN}?${buildContinueSearch(target)}`);

      return true;
    }

    return false;
  };

  const conditionallySetRoutingTarget = (location: Location) => {
    if (requiresAuth(location)) {
      authManager.routingTarget = location;
    }
  };

  const fetchUserInfo = async (isCancelled?: () => boolean) => {
    const isAuthenticated = isLoggedIn();

    let response: Awaited<ReturnType<typeof authManager.tryLogin>>;
    try {
      response = await authManager.tryLogin();
    } catch (error) {
      if (isCancelled?.()) {
        return;
      }

      /*
       * Boot/login fetch failed (expired token, backend unavailable, transient network error).
       * Without this catch the rejection is unhandled and the user is stranded on a blank app with
       * an empty user store and nothing in observability. Log for Datadog, then, if they are on a
       * page that requires auth, route them to /login carrying the intended destination so they get
       * an actionable screen instead of a blank one. The session token is left intact so a transient
       * backend blip does not log the user out — they can simply re-authenticate if it was expired.
       */
      LoggingService.error({ error: error as Error, message: 'RouterRoot: failed to fetch user on boot' });

      const failureLocation = routerRef.current?.location;
      if (failureLocation && requiresAuth(failureLocation)) {
        const target = `${failureLocation.pathname}${failureLocation.search}${failureLocation.hash}`;
        routerRef.current?.replace(`${RoutePath.LOGIN}?${buildContinueSearch(target)}`);
      }

      return;
    }

    if (isCancelled?.()) {
      return;
    }

    LoggingService.setUser(response?.data?.user);

    setUser(
      response?.data?.user
        ? { ...response?.data.user, metadata: response?.data.metadata.mutation.user }
        : ({} as CurrentUser)
    );
    // Always set the lastLogin user context to mirror localStorage on fetch
    setLastLogin(lastLoginStorage.get());

    // Read the live location (not the mount-time closure) so a mid-fetch navigation redirects correctly.
    const continuePath = getSafeContinuePath(routerRef.current?.location?.search ?? '');
    const currentPath = routerRef.current?.location?.pathname as RoutePath;
    if (isAuthenticated && noAuthPaths.includes(currentPath) && !authAgnosticPaths.includes(currentPath)) {
      routerRef.current?.replace(continuePath ?? RoutePath.HOME);
    }
  };

  // Mount-only: replaces componentDidMount
  useEffect(() => {
    let cancelled = false;
    const { location } = router;

    conditionallySetRoutingTarget(location);

    if (requiresUnauthorizedRedirect()) {
      return () => {
        cancelled = true;
      };
    }

    let interval: ReturnType<typeof setInterval> | undefined;
    if (process.env.NODE_ENV === 'production') {
      interval = setInterval(() => void reloadAppIfOutdated(), 1000 * 60 * 5);
    }

    void fetchUserInfo(() => cancelled);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replaces componentDidUpdate — fires on location.pathname change only, after the initial mount.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;

      return;
    }

    const { location } = router;

    if (requiresUnauthorizedRedirect()) {
      return;
    }

    const isAuthenticated = isLoggedIn();

    conditionallySetRoutingTarget(location);

    if ((lastLogin || 0) < (lastLoginStorage.get() || 0)) {
      void fetchUserInfo();
    }

    if (!isAuthenticated) {
      // If not authenticated, clear the user store regardless of whether or not a protected route
      setUser({} as CurrentUser);
    }
    /*
     * lastLogin is a reactive Zustand selector — including it prevents the stale-closure race
     * where a navigation before the mount fetch completes triggers a spurious refetch.
     * fetchUserInfo/conditionallySetRoutingTarget/requiresUnauthorizedRedirect are intentionally excluded.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.location.pathname, lastLogin]);

  return <>{children}</>;
};

export default withRouter(RouterRoot);
